import { Collapsible, CollapsibleContent, CollapsibleTrigger, cn } from "@ndd/ui";
import { ChevronDown } from "lucide-react";
import React, { useState, type ReactElement } from "react";
import type {
  PatientQuickNoteInHealthie,
  PatientQuickNoteRevisionResponse,
} from "../../../../shared/api-types";

/**
 * NO-784: Healthie's quick notes text outside AlwaysAI's block, fetched when
 * the Quick Note opens. Read-only and collapsed until the RD opens it; the
 * text renders as plain text, never as HTML. Nothing renders when the server
 * sent no section (flag off, no Healthie text, or no linked Client).
 */
export function PatientQuickNoteInHealthieSection({
  inHealthie,
  defaultOpen = false,
}: {
  inHealthie: PatientQuickNoteInHealthie | null | undefined;
  defaultOpen?: boolean;
}): ReactElement | null {
  const [open, setOpen] = useState(defaultOpen);

  if (!inHealthie) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <section
        aria-label="In Healthie"
        className="mt-3 rounded-[10px] border border-sand-200 bg-sand-50"
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left"
          >
            <span className="text-[12.5px] font-medium text-foreground">
              In Healthie
            </span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "size-4 shrink-0 text-sand-500 transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-3">
            {inHealthie.status === "ok" ? (
              <>
                <p className="mb-1.5 text-xs text-sand-500">
                  Other quick notes in this patient&apos;s Healthie chart. Read-only
                  here.
                </p>
                <p className="whitespace-pre-wrap break-words text-[12.5px] leading-[18px] text-foreground">
                  {inHealthie.text}
                </p>
              </>
            ) : (
              <p role="status" className="text-[12.5px] text-sand-500">
                Couldn&apos;t load the quick notes in Healthie.
              </p>
            )}
          </div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

/**
 * NO-784: "Edited in Healthie" with the detection time, while the current
 * Revision came from Healthie (the edit itself or the merge built from it).
 * A later RD save makes a new current Revision, which clears it.
 */
export function PatientQuickNoteSourceLabel({
  revision,
  timeZone,
}: {
  revision: PatientQuickNoteRevisionResponse | null;
  timeZone?: string;
}): ReactElement | null {
  if (revision?.source !== "healthie" && revision?.source !== "healthie_merge") {
    return null;
  }

  return (
    <p className="mt-2 text-[12.5px] leading-[18px] text-sand-500">
      <span className="font-medium text-foreground">Edited in Healthie</span>{" "}
      <time dateTime={revision.acceptedAt}>
        {formatDetectedAt(revision.acceptedAt, timeZone)}
      </time>
    </p>
  );
}

function formatDetectedAt(value: string, timeZone: string | undefined): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
}
